import React from "react";
import { Alert } from "react-native";
import { postImagesBatch } from "../services/api";
import { TSession, TSurgeryStageType, TView, TProcedure } from "./types";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

export interface UploadedImage {
  file: File;
  viewType: TView;
  procedureTypes: TProcedure[];
  surgeryStage: TSurgeryStageType;
}

export interface ImageUploadState {
  preop: UploadedImage[];
  markings: UploadedImage[];
  postop: UploadedImage[];
  wish: UploadedImage[];
  isUploading: boolean;
}

/* ------------------------------------------------------------------ */
/*  Initial state                                                     */
/* ------------------------------------------------------------------ */

export const initialImageUploadState: ImageUploadState = {
  preop: [],
  markings: [],
  postop: [],
  wish: [],
  isUploading: false,
};

/* ------------------------------------------------------------------ */
/*  Manager class                                                     */
/* ------------------------------------------------------------------ */

export class ImageUploadManager {
  private state: ImageUploadState;
  private setState: (state: Partial<ImageUploadState>) => void;

  constructor(
    state: ImageUploadState,
    setState: (state: Partial<ImageUploadState>) => void,
  ) {
    this.state = state;
    this.setState = setState;
  }

  /* ---------------- File‑picker (click / tap) --------------------- */

  handleFileSelection = (surgeryStage: TSurgeryStageType) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";

    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files) {
        const newImages: UploadedImage[] = Array.from(files).map((file) => ({
          file,
          viewType: "BODY_FRONT",
          procedureTypes: [],
          surgeryStage,
        }));
        this.addImages(surgeryStage, newImages);
      }
    };

    input.click();
  };

  /* ---------------- File‑drop (DnD) ------------------------------- */

  handleFilesDrop = (surgeryStage: TSurgeryStageType, files: File[]) => {
    const newImages: UploadedImage[] = files.map((file) => ({
      file,
      viewType: "BODY_FRONT",
      procedureTypes: [],
      surgeryStage,
    }));
    this.addImages(surgeryStage, newImages);
  };

  /* ---------------- Image CRUD helpers ---------------------------- */

  addImages = (surgeryStage: TSurgeryStageType, newImages: UploadedImage[]) => {
    const imagesWithStage = newImages.map((img) => ({ ...img, surgeryStage }));

    switch (surgeryStage) {
      case "preop":
        this.setState({ preop: [...this.state.preop, ...imagesWithStage] });
        break;
      case "markings":
        this.setState({
          markings: [...this.state.markings, ...imagesWithStage],
        });
        break;
      case "postop":
        this.setState({ postop: [...this.state.postop, ...imagesWithStage] });
        break;
      case "wish":
        this.setState({
          wish: [...this.state.wish, ...imagesWithStage],
        });
        break;
    }
  };

  removeImage = (surgeryStage: TSurgeryStageType, index: number) => {
    switch (surgeryStage) {
      case "preop":
        this.setState({
          preop: this.state.preop.filter((_, i) => i !== index),
        });
        break;
      case "markings":
        this.setState({
          markings: this.state.markings.filter((_, i) => i !== index),
        });
        break;
      case "postop":
        this.setState({
          postop: this.state.postop.filter((_, i) => i !== index),
        });
        break;
      case "wish":
        this.setState({
          wish: this.state.wish.filter((_, i) => i !== index),
        });
        break;
    }
  };

  updateImageViewType = (
    surgeryStage: TSurgeryStageType,
    index: number,
    viewType: TView,
  ) => {
    const update = (imgs: UploadedImage[]) =>
      imgs.map((img, i) => (i === index ? { ...img, viewType } : img));

    switch (surgeryStage) {
      case "preop":
        this.setState({ preop: update(this.state.preop) });
        break;
      case "markings":
        this.setState({ markings: update(this.state.markings) });
        break;
      case "postop":
        this.setState({ postop: update(this.state.postop) });
        break;
      case "wish":
        this.setState({ wish: update(this.state.wish) });
        break;
    }
  };

  updateImageProcedureTypes = (
    surgeryStage: TSurgeryStageType,
    index: number,
    procedureTypes: TProcedure[],
  ) => {
    const update = (imgs: UploadedImage[]) =>
      imgs.map((img, i) => (i === index ? { ...img, procedureTypes } : img));

    switch (surgeryStage) {
      case "preop":
        this.setState({ preop: update(this.state.preop) });
        break;
      case "markings":
        this.setState({ markings: update(this.state.markings) });
        break;
      case "postop":
        this.setState({ postop: update(this.state.postop) });
        break;
      case "wish":
        this.setState({ wish: update(this.state.wish) });
        break;
    }
  };

  clearStage = (surgeryStage: TSurgeryStageType) => {
    switch (surgeryStage) {
      case "preop":
        this.setState({ preop: [] });
        break;
      case "markings":
        this.setState({ markings: [] });
        break;
      case "postop":
        this.setState({ postop: [] });
        break;
      case "wish":
        this.setState({ wish: [] });
        break;
    }
  };

  clearAllImages = () => {
    this.setState({ preop: [], markings: [], postop: [], wish: [] });
  };

  getAllImages = (): UploadedImage[] => [
    ...this.state.preop,
    ...this.state.markings,
    ...this.state.postop,
    ...this.state.wish,
  ];

  hasImagesToUpload = (): boolean => this.getAllImages().length > 0;

  getImagesByStage = (surgeryStage: TSurgeryStageType): UploadedImage[] => {
    switch (surgeryStage) {
      case "preop":
        return this.state.preop;
      case "markings":
        return this.state.markings;
      case "postop":
        return this.state.postop;
      case "wish":
        return this.state.wish;
      default:
        throw new Error(`Unknown surgeryStage: ${surgeryStage}`);
    }
  };

  /* ---------------- Upload to backend ----------------------------- */

  uploadImages = async (
    session: TSession,
    patientId: string,
    onSuccess?: () => void,
    onError?: (error: unknown) => void,
  ) => {
    const allImages = this.getAllImages();
    if (allImages.length === 0) {
      Alert.alert("No Images", "Please select images to upload first.");
      return;
    }

    this.setState({ isUploading: true });

    try {
      for (const stage of [
        "preop",
        "markings",
        "postop",
        "wish",
      ] as TSurgeryStageType[]) {
        const images = this.getImagesByStage(stage);
        if (!images.length) continue;

        const body = images.map((img) => ({
          viewType: img.viewType,
          procedureTypes: img.procedureTypes,
          file: img.file,
        }));

        await postImagesBatch(session, patientId, body, stage);
      }

      this.clearAllImages();
      Alert.alert("Success", "Images uploaded successfully!");
      onSuccess?.();
    } catch (err) {
      console.error("Error uploading images:", err);
      Alert.alert("Error", "Failed to upload images. Please try again.");
      onError?.(err);
      throw err;
    } finally {
      this.setState({ isUploading: false });
    }
  };
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

export const useImageUpload = () => {
  const [state, setState] = React.useState<ImageUploadState>(
    initialImageUploadState,
  );

  const update = (patch: Partial<ImageUploadState>) =>
    setState((prev) => ({ ...prev, ...patch }));

  const manager = new ImageUploadManager(state, update);

  return {
    state,
    manager,
    handleFileSelection: manager.handleFileSelection,
    handleFilesDrop: manager.handleFilesDrop,
    removeImage: manager.removeImage,
    uploadImages: manager.uploadImages,
    hasImagesToUpload: manager.hasImagesToUpload(),
    clearAllImages: manager.clearAllImages,
  };
};

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

export const IMAGE_CATEGORIES = {
  preop: "Add Preop Images",
  markings: "Add Marking Images",
  postop: "Add Postop Images",
  wish: "Add Wish Photos",
} as const;

export const VIEW_TYPES = [
  "TORSO_FRONT",
  "TORSO_LEFT_45",
  "TORSO_LEFT",
  "TORSO_RIGHT_45",
  "TORSO_RIGHT",
  "BODY_FRONT",
  "BODY_BACK",
  "BODY_LEFT",
  "BODY_RIGHT",
  "HEAD_FRONT",
  "HEAD_LEFT_45",
  "HEAD_LEFT",
  "HEAD_RIGHT_45",
  "HEAD_RIGHT",
] as const;
